<script setup>
import { computed, onMounted, ref } from 'vue'
import { useAuthStore } from "@/store/auth";
import ISearch from "@/assets/img/icon/i-search.vue";
import IDownload from "@/assets/img/icon/i-download.vue";
import IChevron from "@/assets/img/icon/i-chevron.vue";
import { useRouter } from "vue-router";
import { useStore } from "@/store";
import ITrash from "@/assets/img/icon/i-trash.vue";
import { t } from '@/utils/i18n';


const props = defineProps({
  window: {
    type: Object,
    required: false, // window 객체가 없을 수도 있음
  }
});
const authStore = useAuthStore();
const store = useStore();
const router = useRouter();
const isRefreshed = ref(false);
const downloadHover = ref(null);

// Pagenation Set
const selectedPage = ref(1);
const totalPage = computed(() => Math.ceil(store.projectListData.count / 10));
const pageNationArr = ref([]);
const searchType = ref('all');
const searchText = ref('');

const debounceSearchProject = debounce(handleSearchProject, 300);

const flags = ref({
  deleteProject: false,
})
const clickedProjectId = ref(0);

function setPageNation() {
  let temp = [];
  const firstPage = 10 * (selectedPage.value - 1) + 1;
  const lastPage = 10 * selectedPage.value;

  for (let i = 1; i <= totalPage.value; i++) {
    if (i >= firstPage && i <= lastPage) {
      temp.push(i);
    }

    if (temp.length === 10) {
      break;
    }
  }

  pageNationArr.value = temp;
}

function goProject(projectId) {
  router.push({
    name: 'ResultProject',
    query: { projectId: projectId },
  })
}

function saveCurrentPageNum(num = selectedPage.value) {
  localStorage.removeItem('savedPageNum');
  localStorage.setItem('savedPageNum', num);
}

function movePage(page) {
  if (page === -1) { // prev
    if (selectedPage.value - 1 === 0) {
      return;
    }
    selectedPage.value -= 1;
    saveCurrentPageNum();
  } else if (page === 9999) { // next
    if (selectedPage.value === totalPage.value) {
      return;
    }
    selectedPage.value += 1;
    saveCurrentPageNum();
  } else {
    selectedPage.value = page;
    saveCurrentPageNum();
  }
}

function matchFieldIndexWithName(index) {
  if (store.userLanguage !== 'en') {
    const temp = store.fieldListDefault.find(fl => fl.id === index);
    return temp ? temp.name : '';
  } else {
    const temp = store.fieldListDefault.find(fl => fl.id === index);
    return temp ? temp.parameter : '';
  }
}

function getLanguageById(id) {
  if (store.userLanguage !== 'en') {
    const temp = store.languageListDefault.find(pll => pll.id === id);
    return temp.name;
  } else {
    const temp = store.languageListDefault.find(pll => pll.id === id);
    return temp.parameter;
  }
}

function getTranslationModelById(id) {
  const temp = store.translationModelListDefault.find(tml => tml.id === id);
  return temp.model_name;
}

function setProjectCode(id, createdAt) {
  const temp = createdAt.split('T');
  const date = temp[0].replaceAll('-', '');

  return `${date}_${id}`;
}

async function handleSearchProject() {
  try {
    if (searchType.value === '') {
      return
    }
    await store.searchProjectList(searchText.value, searchType.value);
  } catch (e) {
    console.error(e);
  } finally {
    setPageNation();
  }
}

function debounce(func, wait = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(func(...args), wait);
  }
}

async function checkAuthorization() {
  if (!authStore.isAuthenticated) {
    isRefreshed.value = await authStore.refreshToken();

    if (!isRefreshed) {
      await authStore.logout();
      return;
    }
  }

  return true;
}

async function initialize() {
  const isAuthorized = await checkAuthorization();
  if (!isAuthorized) {
    return
  }

  if (!!store.languageListDefault) {
    await store.getLanguageListDefault();
  }
  if (!!store.translationModelListDefault) {
    await store.getTranslationModelListDefault();
  }
  if (!!store.projectListData) {
    await store.getProjectList();
  }

  if (store.fieldListDefault.length === 0) {
    await store.getFieldListDefault();
  }

  setPageNation();

  // direct to show the page number saved before.
  if (!localStorage.getItem('savedPageNum') || isNaN(localStorage.getItem('savedPageNum'))) {
    movePage(1);
  } else {
    const currentPage = parseInt(localStorage.getItem('savedPageNum'));
    selectedPage.value = currentPage;
    movePage(currentPage);
  }
}

async function handleDeleteProject(projectId) {
  flags.value.deleteProject = !flags.value.deleteProject;
  clickedProjectId.value = projectId;
}

async function deleteProject(yn) { // false: cancel, true: delete
  try {
    if (!yn) {
      const target = document.querySelector('.confirm_container');
      target.classList.add('slide_up');

      setTimeout(() => {
        target.classList.remove('slide_up');
        flags.value.deleteProject = yn;
      }, 300)
      return
    }

    const isSuccess = await store.deleteProject(clickedProjectId.value);

    if (isSuccess) {
      flags.value.deleteProject = false;
      window.location.reload();
    }
  } catch (e) {
    console.error("deleteProject", yn);
  }
}

onMounted(async () => {
  await initialize();
})

</script>
<template>
  <div id="projectList" v-if="authStore.isAuthenticated">
    <transition name="fade" mode="out-in">
      <h1 class="title">{{ t('프로젝트 리스트', 'Project List') }}</h1>
    </transition>

    <div class="project_control_wrapper">
      <div class="search_project">
        <transition name="fade" mode="out-in">
          <select class="type_select" v-model="searchType">
            <option value="all">{{ t('전체', 'All') }}</option>
            <option value="display-name">{{ t('프로젝트명', 'Title') }}</option>
            <option value="tamsecode">TAMSE</option>
            <option value="source-language">{{ t('출발어', 'Source') }}</option>
            <option value="target-language">{{ t('도착어', 'Target') }}</option>
            <option value="translation-model-name">{{ t('번역 엔진명', 'Engine') }}</option>
            <option value="project-field">{{ t('분야명', 'Field') }}</option>
          </select>
        </transition>

        <i class="vertical"></i>

        <div class="search_input">
          <label for="searchText"></label>
          <input id="searchText" type="text" :placeholder="t('검색어를 입력하세요', '')" name="searchText" v-model="searchText"
            @keyup.enter="debounceSearchProject" />
          <i-search class="icon size_16 blue" @click="debounceSearchProject" />
        </div>
      </div>

      <div>
        <router-link :to="{ name: 'CreateProject' }" class="btn size_20 primary">
          <transition name="fade" mode="out-in">
            <span>{{ t('프로젝트 추가', 'Create Project') }}</span>
          </transition>
        </router-link>
      </div>
    </div>

    <p class="project_count">
      {{ t(`${store.projectListData.count}개의 프로젝트`, `${store.projectListData.count} Projects`) }}
    </p>

    <div class="table_wrapper">
      <div class="table_header">
        <transition name="fade" mode="out-in">
          <div>No.</div>
        </transition>
        <transition name="fade" mode="out-in">
          <div>{{ t('TAMSE 분야', 'TAMSE') }}</div>
        </transition>
        <transition name="fade" mode="out-in">
          <div>{{ t('프로젝트', 'Project') }}</div>
        </transition>
        <transition name="fade" mode="out-in">
          <div>{{ t('언어 / 분량', 'Lang / Amount') }}</div>
        </transition>
        <transition name="fade" mode="out-in">
          <div>{{ t('번역 엔진', 'Translation Engine') }}</div>
        </transition>
        <transition name="fade" mode="out-in">
          <div>{{ t('다운로드', 'Download') }}</div>
        </transition>
      </div>

      <div v-if="store.projectListData?.count === 0" class="list_empty">
        <img src="@/assets/img/empty_list.png" alt="empty" />
        <transition name="fade" mode="out-in">
          <span>{{ t('프로젝트 내역이 없습니다.', 'There is no project history.') }}</span>
        </transition>
      </div>

      <div v-show="(index + 1) >= 10 * (selectedPage - 1) + 1 && (index + 1) <= 10 * selectedPage"
        v-for="(pl, index) in store.projectListData.result" :key="index" class="table_row">
        <div class="index" @click="goProject(pl.id)">
          {{ store.modifyIndex(store.projectListData.count - index) }}
        </div>

        <div class="tamse_type" @click="goProject(pl.id)">
          <i class="icon tamse size_32" :class="store.setTamseClassName(pl.tamse_code)"></i>
          <transition name="fade" mode="out-in">
            <span>{{ matchFieldIndexWithName(pl.project_field_id) }}</span>
          </transition>
        </div>

        <div class="project_name" @click="goProject(pl.id)">
          <span>
            {{ setProjectCode(pl.id, pl.created_at) }}
            <i-trash class="icon size_16 black_1" @click.stop="handleDeleteProject(pl.id)" />
          </span>
          <p>{{ pl.display_name }}</p>
        </div>

        <div class="language" @click="goProject(pl.id)">
          <transition name="fade" mode="out-in">
            <p>{{ `${getLanguageById(pl.source_language_id)} - ${getLanguageById(pl.target_language_id)}` }}</p>
          </transition>
          <span>{{ pl.string_count.toLocaleString() }}</span>
        </div>

        <div class="engine" @click="goProject(pl.id)">
          {{ pl.is_deprecated ? `${getTranslationModelById(pl.translation_model_id)}(${ store.userLanguage === 'ko' ? '삭제됨' : 'Deleted'})` : getTranslationModelById(pl.translation_model_id) }}
        </div>

        <div class="download">
          <i-download class="icon size_20" @mouseenter="downloadHover = index" />
          <div class="download_hovered_wrapper" :class="{ 'download_hover': downloadHover === index }"
            @mouseleave="downloadHover = null">
            <p @click="store.downloadOriginalDocument(pl.id, pl.display_name)">
              {{ t('원본 파일', 'Original file') }}
            </p>
            <p @click="store.downloadTranslationDocument(pl.id, pl.display_name)">
              {{ t('번역문 파일', 'Translation file') }}
            </p>
            <p @click="store.downloadGlossaryDocument(pl.id, pl.display_name)">
              {{ t('용어집 파일', 'Glossary file') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="pageNationArr.length > 1" class="page_nation">
      <div class="page_move" @click="movePage(-1)">
        <i-chevron class="icon size_20 left" />
      </div>

      <p v-for="(pna, index) in pageNationArr" :key="index" class="page_num"
        :class="{ 'selected': selectedPage === pna }" @click="movePage(index + 1)">{{ pna }}</p>

      <div class="page_move" @click="movePage(9999)">
        <i-chevron class="icon size_20 right" />
      </div>
    </div>

    <div v-if="flags.deleteProject" class="modal_wrapper">
      <div class="confirm_container">
        <p>{{ t('프로젝트를 삭제 할까요 ?', 'Are you sure you want to delete the project?') }}</p>
        <div class="btn_wrapper">
          <button class="btn size_28 grey" @click="deleteProject(false)">
            {{ t('취소', 'Cancel') }}
          </button>
          <button class="btn size_28 red" @click="deleteProject(true)">
            {{ t('삭제하기', 'Delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>